import React, { useState, useEffect } from 'react';
import { RatingQuestion, RadioQuestion, FreeTextQuestion } from './questions';

// Update the RenderQuestion component props interface
interface RenderQuestionProps {
  question: {
    id: number;
    question: string;
    type: 'rating' | 'free_text' | 'radio' | 'checkbox';
    options: string[] | null;
    is_required: number;
  };
  onChange: (questionId: number, value: string | number) => void;
  responses: Record<number, string | number>;
  showValidation: boolean;
  disabled?: boolean; // Make sure this is boolean | undefined
}

const RenderQuestion: React.FC<RenderQuestionProps> = React.memo(({ 
  question, 
  onChange, 
  responses,
  showValidation = false,
  disabled = false
}) => {
  const options = React.useMemo(() => {
    if (!question.options) return null;
    return typeof question.options === 'string' ? 
      JSON.parse(question.options) : 
      question.options;
  }, [question.options]);

  const handleChange = React.useCallback((value: string | number) => {
    onChange(question.id, value);
  }, [onChange, question.id]);

  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (showValidation) {
      const isAnswered = responses[question.id] !== undefined && 
        responses[question.id] !== null && 
        (typeof responses[question.id] === 'string' 
          ? String(responses[question.id]).trim() !== ''
          : responses[question.id] !== '');
      setShowError(question.is_required === 1 && !isAnswered);
    }
  }, [showValidation, question.is_required, responses, question.id]);

  const renderContent = () => {
    const currentValue = responses[question.id];
    
    switch (question.type) {
      case 'rating':
        return (
          <RatingQuestion 
            id={question.id}
            value={currentValue as number}
            onChange={handleChange}
            isRequired={question.is_required === 1}
            disabled={disabled}
          />
        );
      case 'radio':
        return options && (
          <RadioQuestion
            id={question.id}
            options={options}
            value={currentValue as string}
            onChange={handleChange}
            isRequired={question.is_required === 1}
            disabled={disabled}
          />
        );
      case 'free_text':
        return (
          <FreeTextQuestion
            value={currentValue as string}
            onChange={handleChange}
            isRequired={question.is_required === 1}
            disabled={disabled}
          />
        );
      default:
        return null; // Return null instead of error message
    }
  };

  // Add a check for supported question types
  const isSupportedType = (type: string): boolean => {
    return ['rating', 'radio', 'free_text'].includes(type);
  };

  // Return null early if question type is not supported
  if (!isSupportedType(question.type)) {
    return null;
  }

  return (
    <div className={`mb-4 ${showError ? 'has-error' : ''}`}>
      <label className="form-label">
        {question.question}
        {question.is_required === 1 && <span className="text-danger">*</span>}
      </label>
      {renderContent()}
      {showError && <div className="text-danger">This question is required.</div>}
    </div>
  );
});

RenderQuestion.displayName = 'RenderQuestion';

export default RenderQuestion;